import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscriber,
  Subscription,
  timer,
} from 'rxjs';
import {
  catchError,
  delayWhen,
  retryWhen,
  scan,
  takeUntil,
} from 'rxjs/operators';
import { WebSocketSubject } from 'rxjs/webSocket';
import { AnalyticsService } from 'src/app/components/logging/analytics.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { WebSocketService } from 'src/app/shared/services/web-socket.service';
import { environment } from 'src/environments/environment';
import ChannelConfig from '../types/channel-config';
import Member from '../types/member';
import { MessageType } from '../types/message-type';
import SharerAudio, {
  SharerAudioStatus,
  toSharerAudio,
} from '../types/sharer-audio';
import { SignalServerData } from '../types/signal-server-data';
import { WebsocketParams } from '../types/websocket-params';
import { RvshareAppDetectionService } from './rvshare-app-detection.service';
import {
  SenderStatsSnapshot,
  senderSnapshotOf,
  summarizeSenderStats,
} from './sender-stats';
import {
  FIXED_FRAME_WIDTH,
  FixedFrameVideo,
  FrameOrientation,
  getFixedFrameSize,
  isFixedFrameSupported,
  StillPictures,
} from './fixed-frame-video';
import { ModeratorWebsocketParamsService } from '../types/moderator-params';
import { LocalStorageHelperService } from 'src/app/shared/services/local-storage-helper.service';

@Injectable({
  providedIn: 'root',
})
export class ScreenSharingService implements OnDestroy {
  private createPeerConnectionState(displayId: string) {
    return {
      displayId,
      pc: null as RTCPeerConnection | null,
      pendingIceCandidates: [] as RTCIceCandidateInit[],
      remoteDescriptionSet: false,
    };
  }

  socket: WebSocketSubject<SignalServerData>;
  socketSubscription: Subscription;
  channelError: string;
  joinCodeError: string;
  iceConfiguration: RTCConfiguration;
  connecting: boolean = false;
  closing: boolean = false;
  reconnecting: boolean = false;
  selectedDisplayIds: string[] = [];
  displayPeerStates: {
    displayId: string;
    peerConnection: RTCPeerConnection | null;
    remoteDescriptionSet: boolean;
    pendingIceCandidates: RTCIceCandidateInit[];
    avoidLiveResolutionDownscale?: boolean;
    decodeRecoveryIceAttempts?: number;
    decodeRecoveryLastAttemptAt?: number;
    // Set while an ICE-restart offer is being created, so a second recovery can't start another one on it.
    recoveryOfferInFlight?: boolean;
    // When this connection's offer started (performance.now(), which can't step back like the wall clock), until its
    // answer is set: recovery requests wait for that answer.
    offerStartedAt?: number;
    // The id of this connection's latest offer, which the display returns with its answer.
    offerId?: string;
    // A recovery was asked for while the offer waited; it goes ahead if the answer doesn't come.
    recoveryWaiting?: boolean;
    // The answer is being set: the offer still counts as waiting for it, however long it took, so nothing replaces the
    // connection meanwhile, and a second copy of the answer is skipped.
    settingAnswer?: boolean;
    // Fixed-frame encode scale this connection was created with; unset when it sends the raw capture.
    encodeScale?: number;
    // Fixed-frame orientation this connection was created with; unset when it sends the raw capture.
    orientation?: FrameOrientation;
  }[] = [];
  reconnectAttempts: number = 0;
  mediaStream: MediaStream;
  // What is actually sent: the capture composited onto a fixed-size frame, one per display orientation in use.
  private fixedFrames = new Map<
    FrameOrientation,
    { video: FixedFrameVideo; stream: MediaStream }
  >();
  // Orientations whose pipeline failed during this share: 'page' retries the fixed frame composited on the page (after
  // a worker failure), 'raw' sends the raw capture (after the page pipeline failed too).
  private fixedFrameFallbacks = new Map<FrameOrientation, 'page' | 'raw'>();
  // Takes the still-screen pictures for every fixed-frame pipeline of the capture at once.
  private stillPictures: StillPictures | null = null;
  // Last sender stats sample per display, so each answer covers the interval since the previous request.
  private senderStatsSamples = new Map<
    string,
    { peerConnection: RTCPeerConnection; snapshot: SenderStatsSnapshot }
  >();
  // Displays whose sample is still being taken: a slow getStats() must not finish after the next one and go backwards.
  private senderStatsInFlight = new Set<string>();
  // Paused keeps the capture and the sender, and only closes the connections; stream() resumes.
  private paused = false;
  // Set while resetting after the RV Share app reported an error; see resetAfterAppError()
  private appAlreadyClosed = false;
  // In the RV Share app: this page asked its app to share (joinAndStart or start), and the share hasn't ended since
  private appShareRequested = false;
  // In the RV Share app: this page asked its app to join a session (join or joinAndStart), and hasn't left it since
  private appJoined = false;
  // In the RV Share app: this page reloaded and joins again from its URL. Its app is already in the session, sharing,
  // paused or stopped, so the join must not ask it to start another share.
  private rejoinAfterReload = false;
  joinedSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  invitedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );
  started = 0;
  memberDisplayName = '';
  moderator: boolean;
  memberId: string;
  senderId: string;
  invitedId: string;
  participants: Member[];
  displayParticipants: Member[] = [];
  displayParticipant: Member;
  sessionDisplayIds: string[] = [];
  channelTimeout: ReturnType<typeof setTimeout> = null;
  // A display had to mute the video (volume-muted)
  muted = false;
  // Whether the share in progress carries audio, as its sharer reported it
  sharerAudio: SharerAudio | null = null;
  volume: ChannelConfig['volume'] = 100;
  quality: ChannelConfig['quality'] = 'high';
  allowOthersToJoin: boolean = false;
  isModerated = false;
  // Counts leaves, so a screen picker still open when the member leaves (or is removed) can't start sharing after.
  private captureRequest = 0;
  // When the server will end this session (or remove this streamer) for nobody sharing, once it has warned this member.
  idleSessionEndsAt: number | null = null;

  get moderatorCount(): number {
    if (this.participants) {
      return this.participants.filter((item) => item.memberType === 'moderator')
        .length;
    }
    return 0;
  }

  get sharing(): Member | null {
    const member =
      this.participants &&
      this.participants.find((user) => this.isSharingParticipant(user));
    return member ? member : null;
  }

  get memberIsSharing(): boolean {
    if (!this.memberId) {
      return false;
    }

    if (this.mediaStream && this.senderId === this.memberId) {
      return true;
    }

    return this.sharing?.memberId === this.memberId;
  }

  private canRestartStreamForDisplayRecovery(displayId: string): boolean {
    // A paused share has no connections; resuming sends the displays new ones.
    if (!displayId || !this.mediaStream || !this.memberId || this.paused) {
      return false;
    }

    return (
      this.memberIsSharing ||
      this.displayPeerStates.some(
        (state) => state.displayId === displayId && !!state.peerConnection,
      )
    );
  }

  // WebRTC often brings a 'disconnected' connection back by itself within a second or two; only one that stays down
  // this long is replaced.
  private static readonly DISCONNECTED_GRACE_MS = 3_000;
  // A replacement that doesn't connect (the network is still down, say) is replaced again after these delays, the
  // last one repeating, until one connects or the share is interrupted.
  private static readonly DISPLAY_RECONNECT_RETRY_DELAYS_MS = [
    2_000, 4_000, 8_000, 15_000,
  ];
  // A replacement still negotiating gets as long as the display gives a connection before it asks for another one;
  // one that fails sooner is replaced at its retry delay.
  private static readonly DISPLAY_RECONNECT_CONNECT_WAIT_MS = 8_000;

  // Per display: how many replacement connections were sent since it was last connected, and the connection the next
  // one replaces, with its timer.
  private displayReconnects = new Map<
    string,
    {
      attempts: number;
      timer: ReturnType<typeof setTimeout> | null;
      dueAt?: number;
      peerConnection?: RTCPeerConnection;
    }
  >();

  /**
   * Replaces the display's connection after `delayMs` unless it comes back first, and keeps replacing it, with
   * increasing delays, until a replacement connects. Brings a share back after a network drop without waiting for
   * the display to ask for it.
   */
  private scheduleDisplayReconnect(
    displayId: string,
    peerConnection: RTCPeerConnection,
    delayMs: number,
  ): void {
    // A connection replaced since has nothing left to replace.
    if (
      peerConnection !==
      this.displayPeerStates.find((state) => state.displayId === displayId)
        ?.peerConnection
    ) {
      return;
    }
    const reconnect = this.displayReconnects.get(displayId) ?? {
      attempts: 0,
      timer: null,
    };
    this.displayReconnects.set(displayId, reconnect);
    const retryDelays = ScreenSharingService.DISPLAY_RECONNECT_RETRY_DELAYS_MS;
    const backoff =
      reconnect.attempts === 0
        ? 0
        : retryDelays[Math.min(reconnect.attempts, retryDelays.length) - 1];
    const delay = Math.max(delayMs, backoff);
    const dueAt = Date.now() + delay;
    if (reconnect.timer !== null) {
      // Only ever sooner for the same connection, e.g. one that fails during its grace period, or a replacement that
      // fails before its connect wait is over. One pending for a connection replaced since (the display asked for a
      // new one, say) would do nothing when it fires, so this one takes its place.
      if (
        reconnect.peerConnection === peerConnection &&
        dueAt >= reconnect.dueAt
      ) {
        return;
      }
      clearTimeout(reconnect.timer);
    }
    reconnect.peerConnection = peerConnection;
    reconnect.dueAt = dueAt;
    reconnect.timer = setTimeout(() => {
      reconnect.timer = null;
      this.reconnectDisplay(displayId, peerConnection);
    }, delay);
  }

  private reconnectDisplay(
    displayId: string,
    peerConnection: RTCPeerConnection,
  ): void {
    const displayPeerState = this.displayPeerStates.find(
      (state) => state.displayId === displayId,
    );
    // Replaced, closed or back in the meantime, or the share stopped or paused: nothing to do.
    if (
      displayPeerState?.peerConnection !== peerConnection ||
      peerConnection.connectionState === 'connected' ||
      !this.canRestartStreamForDisplayRecovery(displayId)
    ) {
      return;
    }
    const reconnect = this.displayReconnects.get(displayId);
    if (reconnect) {
      reconnect.attempts++;
    }
    console.log(
      'Connection to display %s did not come back; sending a new one (attempt %d).',
      displayId,
      reconnect?.attempts,
    );
    this.closePeerConnection(displayId);
    this.stream([displayId]);
    const replacement = this.displayPeerStates.find(
      (state) => state.displayId === displayId,
    )?.peerConnection;
    if (replacement) {
      this.scheduleDisplayReconnect(
        displayId,
        replacement,
        ScreenSharingService.DISPLAY_RECONNECT_CONNECT_WAIT_MS,
      );
    }
  }

  /** The display's connection is up: stop replacing it, and drop the interruption once no display is waiting. */
  private endDisplayReconnect(displayId: string): void {
    const reconnect = this.displayReconnects.get(displayId);
    if (reconnect?.timer) {
      clearTimeout(reconnect.timer);
    }
    this.displayReconnects.delete(displayId);
    if (this.displayReconnects.size === 0 && this.channelTimeout) {
      this.clearChannelTimeout();
      this.channelError = '';
    }
  }

  /** Back online: send the waiting displays their new connection now rather than at their next retry. */
  private reconnectDisplaysNow(): void {
    this.displayReconnects.forEach((reconnect, displayId) => {
      if (reconnect.timer === null || !reconnect.peerConnection) {
        return;
      }
      clearTimeout(reconnect.timer);
      reconnect.timer = null;
      this.reconnectDisplay(displayId, reconnect.peerConnection);
    });
  }

  private clearDisplayReconnects(): void {
    this.displayReconnects.forEach(({ timer }) => timer && clearTimeout(timer));
    this.displayReconnects.clear();
  }

  // ICE-restart recoveries escalate to a new peer connection only when they come back to back. Displays also rejoin
  // (and get a recovery offer) every time Cloud Run ends their websocket, once an hour, and those must not add up.
  private static readonly DECODE_RECOVERY_ATTEMPT_WINDOW_MS = 2 * 60_000;

  // An answer comes within a second or two. Less than the display's own wait for a connection (8s), so its next
  // request after an answer that went astray is never held up.
  private static readonly ANSWER_WAIT_MS = 5_000;

  // Size and bitrate go together: fewer pixels spend a low bitrate better, especially on video. High/Medium allow
  // 30fps because screen capture only emits frames when the screen changes, so the cap only matters for motion
  // (e.g. a shared video). 'ultra' is not offered and maps to high: templates are 1920x1080.
  private static readonly QUALITY_PROFILES = {
    high: {
      width: 1920,
      height: 1080,
      maxFramerate: 30,
      maxBitrate: 4_000_000,
    },
    medium: {
      width: 1280,
      height: 720,
      maxFramerate: 30,
      maxBitrate: 2_000_000,
    },
    low: { width: 640, height: 360, maxFramerate: 15, maxBitrate: 600_000 },
  };

  // While the captured screen is still, only keepalives of the same frame go out, yet some encoders (Chromebooks')
  // still spend the whole bitrate on them: about 4Mbps for a slide. So the bitrate is capped until the screen changes
  // again. 1.2Mbps is as low as Chrome on ChromeOS goes for screen content (the 'detail' content hint), and slide
  // changes stayed sharp at it; encoders that send less on a still screen, like macOS's, aren't held back by it.
  private static readonly STILL_SCREEN_MAX_BITRATE = 1_200_000;

  private getQualityProfile(quality: ChannelConfig['quality'] = this.quality) {
    return ScreenSharingService.QUALITY_PROFILES[
      quality === 'medium' || quality === 'low' ? quality : 'high'
    ];
  }

  get videoConstraints(): MediaTrackConstraints {
    if (this.rvshareAppDetectionService.shouldDisableVideoQualitySetting()) {
      return { width: {}, height: {} };
    }

    const profile = this.getQualityProfile();
    return {
      width: { ideal: profile.width, max: profile.width },
      height: { ideal: profile.height, max: profile.height },
      frameRate: { ideal: profile.maxFramerate, max: profile.maxFramerate },
    };
  }

  private getVideoQualityTarget(
    quality: ChannelConfig['quality'] = this.quality,
  ): { width: number; height: number } | null {
    if (this.rvshareAppDetectionService.shouldDisableVideoQualitySetting()) {
      return null;
    }

    const { width, height } = this.getQualityProfile(quality);
    return { width, height };
  }

  private getVideoEncodingLimits(): {
    maxBitrate: number;
    maxFramerate: number;
  } | null {
    if (this.rvshareAppDetectionService.shouldDisableVideoQualitySetting()) {
      return null;
    }

    const { maxBitrate, maxFramerate } = this.getQualityProfile();
    return { maxBitrate, maxFramerate };
  }

  /** How much to scale the fixed 1920-long-side frame down for a quality (1920 → 1, 1280 → 1.5, 640 → 3). */
  private getFixedFrameEncodeScale(
    quality: ChannelConfig['quality'] = this.quality,
  ): number {
    const target = this.getVideoQualityTarget(quality);
    if (!target) {
      return 1;
    }
    return Math.max(
      1,
      FIXED_FRAME_WIDTH / Math.max(target.width, target.height),
    );
  }

  private isDisplayParticipant(displayId: string): boolean {
    return this.displayParticipants.some(
      (m) => m.memberId === displayId || m.displayId === displayId,
    );
  }

  private getDisplayOrientation(displayId: string): FrameOrientation {
    const displayMember = this.displayParticipants.find(
      (m) => m.memberId === displayId || m.displayId === displayId,
    );
    return displayMember?.orientation === 'portrait' ? 'portrait' : 'landscape';
  }

  /**
   * The stream sent to a display: the capture letterboxed into a constant 1920x1080 (or 1080x1920) frame, so the
   * display's decoder never sees the frame size change when the shared tab/window resizes or goes fullscreen.
   * Falls back to the raw capture where Insertable Streams are unavailable (Firefox, Safari).
   */
  private getOutboundStream(displayId: string): MediaStream {
    const capture = this.mediaStream;
    const orientation = this.getDisplayOrientation(displayId);
    const existing = this.fixedFrames.get(orientation);
    if (existing) {
      return existing.stream;
    }

    const videoTrack = capture
      .getTracks()
      .find((track) => track.kind === 'video');
    if (
      !videoTrack ||
      !isFixedFrameSupported() ||
      this.fixedFrameFallbacks.get(orientation) === 'raw'
    ) {
      return capture;
    }

    try {
      if (this.stillPictures?.source !== videoTrack) {
        this.stillPictures = new StillPictures(videoTrack);
      }
      const { width, height } = getFixedFrameSize(orientation);
      const video: FixedFrameVideo = new FixedFrameVideo(
        videoTrack,
        width,
        height,
        (reason) => this.handleFixedFrameFailure(orientation, video, reason),
        {
          useWorker: this.fixedFrameFallbacks.get(orientation) !== 'page',
          onStillChange: () => this.applyStillScreenBitrate(video),
          stillPictures: this.stillPictures,
        },
      );
      video.track.contentHint = 'detail';
      const stream = new MediaStream([
        video.track,
        ...capture.getTracks().filter((track) => track.kind !== 'video'),
      ]);
      this.fixedFrames.set(orientation, { video, stream });
      return stream;
    } catch (error) {
      console.warn(
        'Fixed-frame video unavailable; sending the raw capture.',
        error,
      );
      return capture;
    }
  }

  /** Which pipeline produces the video sent on this connection: the fixed frame (worker or page) or the raw capture. */
  private videoPipelineOf(
    peerConnection: RTCPeerConnection,
  ): string | undefined {
    const track = this.videoTrackOf(peerConnection);
    if (!track) {
      return undefined;
    }
    const video = this.fixedFrameVideoOf(peerConnection);
    if (!video) {
      return 'raw';
    }
    return video.usesWorker ? 'worker' : 'page';
  }

  private videoTrackOf(
    peerConnection: RTCPeerConnection,
  ): MediaStreamTrack | null | undefined {
    return peerConnection
      .getSenders()
      .find((sender) => sender.track?.kind === 'video')?.track;
  }

  /** The fixed-frame pipeline sending on this connection; none when it sends the raw capture. */
  private fixedFrameVideoOf(
    peerConnection: RTCPeerConnection,
  ): FixedFrameVideo | undefined {
    const track = this.videoTrackOf(peerConnection);
    return track
      ? [...this.fixedFrames.values()].find(
          ({ video }) => video.track === track,
        )?.video
      : undefined;
  }

  /**
   * Answers a display's sender-stats-request with our side of its connection. Only the member sending to that display
   * has a connection for it, so in a moderated session the other members stay quiet.
   */
  private async sendSenderStats(displayId: string): Promise<void> {
    const peerConnection = this.displayPeerStates.find(
      (state) => state.displayId === displayId,
    )?.peerConnection;
    if (
      !peerConnection ||
      peerConnection.connectionState !== 'connected' ||
      this.senderStatsInFlight.has(displayId)
    ) {
      return;
    }

    let report: RTCStatsReport;
    this.senderStatsInFlight.add(displayId);
    try {
      report = await peerConnection.getStats();
    } catch (error) {
      console.warn(
        'Sender stats sample failed for display %s',
        displayId,
        error,
      );
      return;
    } finally {
      this.senderStatsInFlight.delete(displayId);
    }

    // The connection was closed or replaced while sampling; this sample describes one that's gone.
    const currentPeerConnection = this.displayPeerStates.find(
      (state) => state.displayId === displayId,
    )?.peerConnection;
    if (
      currentPeerConnection !== peerConnection ||
      peerConnection.connectionState !== 'connected'
    ) {
      return;
    }

    const snapshot = senderSnapshotOf(report);
    const previous = this.senderStatsSamples.get(displayId);
    this.senderStatsSamples.set(displayId, { peerConnection, snapshot });
    const stats = {
      ...summarizeSenderStats(
        snapshot,
        // A new connection's counters start from zero; don't subtract the old connection's.
        previous?.peerConnection === peerConnection ? previous.snapshot : null,
      ),
      context: {
        pipeline: this.videoPipelineOf(peerConnection),
        // A hidden Rise Apps tab gets its main thread throttled.
        hidden: document.visibilityState === 'hidden',
        userAgent: navigator.userAgent,
      },
    };
    console.log('Screen sharing sender stats for display %s', displayId, stats);
    this.socket?.next({ type: MessageType.SENDER_STATS, displayId, stats });
  }

  private isFixedFrameTrack(track: MediaStreamTrack | null): boolean {
    return [...this.fixedFrames.values()].some(
      (fixedFrame) => fixedFrame.video.track === track,
    );
  }

  /**
   * A fixed-frame pipeline stopped producing frames, so every display sent its track would freeze on the last frame.
   * Drop it and restart those displays. A worker pipeline is retried on the page first: the raw capture changes size
   * whenever the shared window does, which is what breaks the K-series decoder. Only if the page pipeline fails too do
   * they get the raw capture, for the rest of the share, so a pipeline that keeps failing can't loop.
   */
  private handleFixedFrameFailure(
    orientation: FrameOrientation,
    video: FixedFrameVideo,
    reason: unknown,
  ): void {
    if (this.fixedFrames.get(orientation)?.video !== video) {
      return;
    }

    const fallback = video.usesWorker ? 'page' : 'raw';
    console.warn(
      'Fixed-frame pipeline for %s displays failed; restarting them with %s.',
      orientation,
      fallback === 'page'
        ? 'the fixed frame composited on the page'
        : 'the raw capture',
      reason,
    );
    this.fixedFrames.delete(orientation);
    this.fixedFrameFallbacks.set(orientation, fallback);

    const affectedDisplayIds = this.displayPeerStates
      .filter((state) =>
        state.peerConnection
          ?.getSenders()
          .some((sender) => sender.track === video.track),
      )
      .map((state) => state.displayId);
    video.stop();

    if (affectedDisplayIds.length && this.mediaStream) {
      this.closePeerConnection(affectedDisplayIds);
      this.stream(affectedDisplayIds);
    }
  }

  private stopFixedFrames(): void {
    this.fixedFrames.forEach(({ video }) => video.stop());
    this.fixedFrames.clear();
    this.fixedFrameFallbacks.clear();
    this.stillPictures = null;
  }

  private preferHardwareVideoCodec(peerConnection: RTCPeerConnection): void {
    const rtpSender = globalThis.RTCRtpSender;
    if (typeof rtpSender?.getCapabilities !== 'function') {
      return;
    }

    const capabilities = rtpSender.getCapabilities('video');
    if (!capabilities?.codecs?.length) {
      return;
    }

    const mimeOrder = ['video/h264', 'video/vp9', 'video/vp8'];
    const ordered = mimeOrder.flatMap((mime) =>
      capabilities.codecs.filter(
        (codec) => codec.mimeType.toLowerCase() === mime,
      ),
    );
    const remaining = capabilities.codecs.filter(
      (codec) => !mimeOrder.includes(codec.mimeType.toLowerCase()),
    );
    const preferred = [...ordered, ...remaining];

    peerConnection.getTransceivers?.().forEach((transceiver) => {
      if (
        transceiver.sender?.track?.kind !== 'video' ||
        typeof transceiver.setCodecPreferences !== 'function'
      ) {
        return;
      }

      transceiver.setCodecPreferences(preferred);
    });
  }

  /** The quality's bitrate, capped while the connection's fixed-frame screen is still. */
  private videoMaxBitrate(
    peerConnection: RTCPeerConnection,
    maxBitrate: number,
  ): number {
    return this.fixedFrameVideoOf(peerConnection)?.still
      ? Math.min(maxBitrate, ScreenSharingService.STILL_SCREEN_MAX_BITRATE)
      : maxBitrate;
  }

  /** Caps or restores the bitrate of every connection sending this fixed-frame video, after its screen went still or changed. */
  private applyStillScreenBitrate(video: FixedFrameVideo): void {
    const limits = this.getVideoEncodingLimits();
    if (!limits) {
      return;
    }

    this.displayPeerStates.forEach(({ peerConnection }) => {
      if (!peerConnection || this.fixedFrameVideoOf(peerConnection) !== video) {
        return;
      }
      const sender = peerConnection
        .getSenders()
        .find((s) => s.track === video.track);
      if (
        !sender ||
        typeof sender.getParameters !== 'function' ||
        typeof sender.setParameters !== 'function'
      ) {
        return;
      }
      const parameters = sender.getParameters();
      if (!parameters.encodings?.length) {
        return;
      }
      parameters.encodings[0].maxBitrate = this.videoMaxBitrate(
        peerConnection,
        limits.maxBitrate,
      );
      void sender.setParameters(parameters).catch((error) => {
        console.warn('Failed to apply the still-screen bitrate', error);
      });
    });
  }

  /**
   * `initial` is true only for a connection that hasn't sent media yet: a fixed-frame track's encoded size is set
   * there and never changed on a live connection (a mid-stream size change kills some hardware decoders).
   */
  private applyVideoSenderParameters(
    peerConnection: RTCPeerConnection,
    options: { allowDownscale?: boolean; initial?: boolean } = {},
  ): void {
    const limits = this.getVideoEncodingLimits();
    if (!limits) {
      return;
    }

    const allowDownscale = options.allowDownscale ?? true;
    const target = this.getVideoQualityTarget();

    peerConnection.getSenders?.().forEach((sender) => {
      if (
        sender.track?.kind !== 'video' ||
        typeof sender.getParameters !== 'function' ||
        typeof sender.setParameters !== 'function'
      ) {
        return;
      }

      const parameters = sender.getParameters();
      if (!parameters.encodings?.length) {
        return;
      }

      parameters.degradationPreference = 'maintain-resolution';

      let scaleResolutionDownBy = 1;
      const fixedFrame = this.isFixedFrameTrack(sender.track);
      if (
        !fixedFrame &&
        target &&
        typeof sender.track.getSettings === 'function'
      ) {
        const settings = sender.track.getSettings();
        const width = settings.width ?? 0;
        const height = settings.height ?? 0;
        if (width && height) {
          scaleResolutionDownBy = Math.max(
            1,
            width / target.width,
            height / target.height,
          );
        }
      }

      const currentScale = parameters.encodings[0].scaleResolutionDownBy ?? 1;
      if (fixedFrame) {
        // Size is chosen once per connection; a quality change that changes it restarts the connection.
        scaleResolutionDownBy = options.initial
          ? this.getFixedFrameEncodeScale()
          : currentScale;
      } else if (!allowDownscale && scaleResolutionDownBy > currentScale) {
        scaleResolutionDownBy = currentScale;
      }

      parameters.encodings[0].maxBitrate = this.videoMaxBitrate(
        peerConnection,
        limits.maxBitrate,
      );
      parameters.encodings[0].maxFramerate = limits.maxFramerate;
      parameters.encodings[0].scaleResolutionDownBy = scaleResolutionDownBy;

      void sender.setParameters(parameters).catch((error) => {
        console.warn('Failed to apply video sender parameters', error);
      });
    });
  }

  private readonly maxReconnectAttempts = 5; // Adjust this as needed
  private readonly reconnectInterval = 5000; // Adjust this as needed (milliseconds)
  private destroy$ = new Subject<void>();
  private currentWebsocketParams: WebsocketParams;
  private readonly onOnline = () => this.reconnectDisplaysNow();

  constructor(
    private webSocketService: WebSocketService,
    private rvshareAppDetectionService: RvshareAppDetectionService,
    private analyticsFactory: AnalyticsService,
    private userStateService: UserStateService,
    private localStorageHelperService: LocalStorageHelperService,
  ) {
    window.addEventListener('online', this.onOnline);
  }

  ngOnDestroy(): void {
    window.removeEventListener('online', this.onOnline);
    // Also ends any reconnect, and leaves a connection's late state change nothing to act on.
    this.closePeerConnection();
  }

  private findParticipantIdFromMobileSender(senderId: string): string {
    if (!this.participants || this.participants.length === 0) {
      return '';
    }

    const participant = this.participants.find(
      (member) => member.mobileMemberId === senderId,
    );

    return participant ? participant.memberId : '';
  }

  /**
   * Resolves which display peer connection a signal belongs to. The server
   * sometimes omits `displayId` on answers/ICE; `senderId` then identifies the
   * display participant. Without this, multi-display sessions route every signal
   * to `displayPeerStates[0]` and the second answer hits RTCPeerConnection in
   * `stable` (InvalidStateError).
   */
  private findDisplayPeerStateForSignal(
    displayId?: string,
    senderId?: string,
  ): (typeof this.displayPeerStates)[number] | undefined {
    if (displayId) {
      const byDisplayId = this.displayPeerStates.find(
        (c) => c.displayId === displayId,
      );
      if (byDisplayId) {
        return byDisplayId;
      }
    }
    if (senderId) {
      const bySenderAsDisplayId = this.displayPeerStates.find(
        (c) => c.displayId === senderId,
      );
      if (bySenderAsDisplayId) {
        return bySenderAsDisplayId;
      }
      const displayMember = this.displayParticipants.find(
        (m) => m.memberId === senderId || m.displayId === senderId,
      );
      if (displayMember?.displayId) {
        return this.displayPeerStates.find(
          (c) => c.displayId === displayMember.displayId,
        );
      }
    }
    if (this.displayPeerStates.length === 1) {
      return this.displayPeerStates[0];
    }
    return undefined;
  }

  private associateMobileIds(members?: Member[]): void {
    if (!members) {
      return;
    }

    members.forEach((member) => {
      if (
        !['mobile', 'mobile_ios', 'mobile_android'].includes(member.memberType)
      ) {
        return;
      }

      const participant = this.participants.find(
        (participant) => participant.memberId === member.appsMemberId,
      );

      if (!participant) {
        return;
      }

      participant.mobileMemberId = member.memberId;
    });
  }

  private isSharingParticipant(member: Member): boolean {
    if (!member) {
      return false;
    }

    return (
      !!member.sharing ||
      (!!this.senderId &&
        [member.memberId, member.appsMemberId, member.mobileMemberId].includes(
          this.senderId,
        ))
    );
  }

  private filterParticipants(members?: Member[]): void {
    if (members) {
      this.participants = members
        .filter((member) =>
          ['moderator', 'streamer'].includes(member.memberType),
        )
        .sort((a) => (a.memberType === 'moderator' ? -1 : 0));
    }

    this.displayParticipants = (members ?? []).filter(
      (member) => member.memberType === 'display',
    );
    this.displayParticipant = this.displayParticipants.find(
      (member) => member.memberType === 'display',
    );

    this.associateMobileIds(members);

    const activeSharer = this.participants?.find((member) => member.sharing);
    if (activeSharer) {
      this.senderId = activeSharer.memberId;
    }

    // In the RV Share app the app sends the stream and keeps its socket up while it shares. If this page thinks its
    // app is sharing but the app's member has left, the app stopped and its stop was missed, e.g. while this page's
    // socket was down after Android cut the network of the backgrounded browser.
    if (
      members &&
      this.rvshareAppDetectionService.isScreenShareApp() &&
      !this.mediaStream &&
      !!this.senderId &&
      this.senderId === this.memberId &&
      !this.appMemberInSession()
    ) {
      console.log(
        'The app sharing for this page has left; marking the share stopped.',
      );
      this.closeStream();
    }
  }

  // Whether this page's RV Share app is in the session, from the last member list
  private appMemberInSession(): boolean {
    return !!this.participants?.find(
      (participant) => participant.memberId === this.memberId,
    )?.mobileMemberId;
  }

  private clearSharingParticipantState(senderId?: string): void {
    if (!this.participants?.length) {
      return;
    }

    const activeSenderId = senderId || this.senderId;
    this.participants.forEach((member) => {
      if (
        member.sharing &&
        (!activeSenderId ||
          [
            member.memberId,
            member.appsMemberId,
            member.mobileMemberId,
          ].includes(activeSenderId))
      ) {
        member.sharing = false;
      }
    });
  }

  private getTargetDisplayIds(): string[] {
    const displayIds =
      this.sessionDisplayIds.length > 0
        ? this.sessionDisplayIds
        : this.currentWebsocketParams?.displayIds?.length
          ? this.currentWebsocketParams.displayIds
          : this.selectedDisplayIds.length > 0
            ? this.selectedDisplayIds
            : this.currentWebsocketParams?.displayId
              ? [this.currentWebsocketParams.displayId]
              : [];

    return [...new Set(displayIds.filter(Boolean))];
  }

  private isPeerConnected(displayId: string): boolean {
    return (
      this.displayPeerStates.find((state) => state.displayId === displayId)
        ?.peerConnection?.connectionState === 'connected'
    );
  }

  private restartStreamForDisplay(
    displayId: string,
    options: { forceFullReconnect?: boolean } = {},
  ): void {
    this.clearChannelTimeout();
    const displayPeerState = this.displayPeerStates.find(
      (state) => state.displayId === displayId,
    );
    const peerConnection = displayPeerState?.peerConnection;
    const now = Date.now();
    const offerStartedAt = displayPeerState?.offerStartedAt;
    // A display that lost its stream asks several ways at once (it rejoins, and asks on its new and old websockets).
    // A second offer meanwhile would only replace one that is about to be answered, and a display that doesn't return
    // the offer id would have its first answer taken for the second's: the requests wait for that answer instead,
    // forced or not.
    const settingAnswer = !!displayPeerState?.settingAnswer;
    const answerWaitNow = performance.now();
    const offerAwaitingAnswer =
      offerStartedAt !== undefined &&
      answerWaitNow - offerStartedAt < ScreenSharingService.ANSWER_WAIT_MS;
    if (displayPeerState && (settingAnswer || offerAwaitingAnswer)) {
      // An answer being set settles held requests, or replaces the connection if it fails.
      if (!settingAnswer && offerStartedAt !== undefined) {
        this.recoverIfNoAnswer(displayPeerState, offerStartedAt, answerWaitNow);
      }
      return;
    }
    // An ICE restart whose offer is still being made after the wait above: only one may be in flight, since a display
    // that doesn't return the offer id would have the first answer taken for the second's. A forced full reconnect
    // still goes ahead: the in-flight offer notices its connection was replaced and stops.
    if (
      displayPeerState?.recoveryOfferInFlight &&
      !options.forceFullReconnect
    ) {
      console.log(
        'Recovery offer already in flight for display %s; ignoring.',
        displayId,
      );
      return;
    }
    const lastAttemptAt = displayPeerState?.decodeRecoveryLastAttemptAt ?? 0;
    const iceAttempts =
      now - lastAttemptAt >
      ScreenSharingService.DECODE_RECOVERY_ATTEMPT_WINDOW_MS
        ? 0
        : (displayPeerState?.decodeRecoveryIceAttempts ?? 0);
    if (displayPeerState) {
      displayPeerState.decodeRecoveryLastAttemptAt = now;
    }
    const useFullReconnect =
      options.forceFullReconnect === true || iceAttempts >= 2;

    if (
      !useFullReconnect &&
      this.mediaStream &&
      displayPeerState &&
      peerConnection &&
      peerConnection.connectionState !== 'closed' &&
      // Anything else (e.g. have-local-offer) means an offer is still waiting for its answer; start over instead.
      peerConnection.signalingState === 'stable'
    ) {
      displayPeerState.decodeRecoveryIceAttempts = iceAttempts + 1;
      void this.sendRecoveryOffer(displayPeerState);
      return;
    }

    if (displayPeerState) {
      displayPeerState.decodeRecoveryIceAttempts = 0;
    }
    this.closePeerConnection(displayId);
    this.stream([displayId]);
  }

  // Goes ahead with a recovery asked for while the display's offer waited, if its answer doesn't come
  private recoverIfNoAnswer(
    displayPeerState: (typeof this.displayPeerStates)[number],
    offerStartedAt: number,
    now: number,
  ): void {
    if (displayPeerState.recoveryWaiting) {
      return;
    }
    console.log(
      'Offer for display %s is waiting for its answer; recovering if it does not come.',
      displayPeerState.displayId,
    );
    displayPeerState.recoveryWaiting = true;
    const peerConnection = displayPeerState.peerConnection;
    setTimeout(
      () => {
        // The answer came (or is being set, or a newer offer replaced this one), or the share paused or ended
        if (
          !displayPeerState.recoveryWaiting ||
          displayPeerState.settingAnswer ||
          displayPeerState.offerStartedAt !== offerStartedAt ||
          displayPeerState.peerConnection !== peerConnection ||
          !this.displayPeerStates.includes(displayPeerState) ||
          !this.mediaStream
        ) {
          return;
        }
        console.log(
          'No answer from display %s; restarting its connection.',
          displayPeerState.displayId,
        );
        this.closePeerConnection(displayPeerState.displayId);
        this.stream([displayPeerState.displayId]);
      },
      ScreenSharingService.ANSWER_WAIT_MS - (now - offerStartedAt),
    );
  }

  private async sendRecoveryOffer(
    displayPeerState: (typeof this.displayPeerStates)[number],
  ): Promise<void> {
    const peerConnection = displayPeerState.peerConnection;
    if (!peerConnection || !this.mediaStream) {
      this.closePeerConnection(displayPeerState.displayId);
      this.stream([displayPeerState.displayId]);
      return;
    }

    displayPeerState.remoteDescriptionSet = false;
    displayPeerState.pendingIceCandidates = [];
    displayPeerState.recoveryOfferInFlight = true;
    displayPeerState.offerStartedAt = performance.now();
    displayPeerState.recoveryWaiting = false;
    const offerId = crypto.randomUUID();
    displayPeerState.offerId = offerId;
    // A full reconnect (or the share stopping) can replace this connection while we await; then stop quietly.
    const isCurrent = () =>
      this.displayPeerStates.includes(displayPeerState) &&
      displayPeerState.peerConnection === peerConnection;

    try {
      this.preferHardwareVideoCodec(peerConnection);
      this.applyVideoSenderParameters(peerConnection);
      const offer = await peerConnection.createOffer({
        iceRestart: true,
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
      });
      if (!isCurrent()) {
        return;
      }
      if (!offer?.sdp) {
        throw new Error('Recovery offer missing SDP');
      }
      await peerConnection.setLocalDescription(offer);
      if (!isCurrent()) {
        return;
      }
      this.socket.next({
        type: MessageType.OFFER,
        sdp: offer.sdp,
        displayId: displayPeerState.displayId,
        offerId,
      });
    } catch (error) {
      if (!isCurrent()) {
        return;
      }
      console.warn(
        'ICE restart recovery offer failed; falling back to new peer connection.',
        error,
      );
      this.closePeerConnection(displayPeerState.displayId);
      this.stream([displayPeerState.displayId]);
    } finally {
      // A connection that replaced this one on the same entry has its own (stream() resets it); a closed connection
      // may never settle its offer at all.
      if (isCurrent()) {
        displayPeerState.recoveryOfferInFlight = false;
      }
    }
  }

  private updateConfig(config: ChannelConfig): void {
    this.volume = config?.volume ?? this.volume;
    this.quality = config?.quality ?? this.quality;
  }

  join(websocketParams: WebsocketParams, rejoinAfterReload = false): void {
    console.log('Joining channel with join code: ', websocketParams.joinCode);

    this.rejoinAfterReload = rejoinAfterReload;
    this.channelError = '';
    this.joinCodeError = '';
    this.memberDisplayName = websocketParams.memberDisplayName;
    this.connecting = true;
    this.closing = false;
    this.idleSessionEndsAt = null;
    this.moderator = websocketParams.isModerator();
    if (this.moderator) {
      this.isModerated = true;
    }

    this.currentWebsocketParams = websocketParams;

    this.connect().subscribe({
      next: (data) => {
        console.log('streamer onmessage', data);

        switch (data.type) {
          case MessageType.JOINED_CHANNEL:
            console.log('Joined a new channel', data);
            this.channelError = '';
            this.joinCodeError = '';
            // A rejoin (e.g. Android cutting a backgrounded page's socket) restarts the server's idle count without an
            // idle-session-active. If the session is still idle, the server warns again on its next health check.
            this.idleSessionEndsAt = null;
            this.iceConfiguration = data.iceConfiguration;
            this.filterParticipants(data.members);
            this.sessionDisplayIds = data.displayIds?.length
              ? data.displayIds
              : data.displayId
                ? [data.displayId]
                : this.sessionDisplayIds;
            if (this.sessionDisplayIds.length > 0) {
              this.selectedDisplayIds = [...this.sessionDisplayIds];
            }
            this.updateConfig(data.config);
            this.sharerAudio = toSharerAudio(
              data.sharerAudio?.memberId,
              data.sharerAudio?.status,
            );
            this.isModerated = data.screenSharingMode === 'moderated';
            this.allowOthersToJoin = data.allowOthersToJoin;

            if (this.senderId && this.memberId === this.senderId) {
              this.senderId = data.memberId;
            }

            const previousMemberId = this.memberId;
            this.memberId = data.memberId;

            // The page learns that its RV Share app shares from the app's offer, which it misses while its own socket
            // is down (e.g. Android cuts the backgrounded browser while the capture prompt covers it). The app also
            // joins without sharing (a moderated join), so its member being in the session means it is sharing only
            // if this page asked it to share. Before JOINED_CHANNEL is emitted, which decides whether the page shows
            // the share as stopped.
            if (
              this.rvshareAppDetectionService.isScreenShareApp() &&
              this.reconnecting &&
              this.appShareRequested &&
              !this.mediaStream &&
              !this.senderId &&
              this.appMemberInSession()
            ) {
              this.senderId = this.memberId;
            }

            this.joinedSubject.next(MessageType.JOINED_CHANNEL);

            if (this.rvshareAppDetectionService.isScreenShareApp()) {
              // The app keeps its own signaling connection, so this page's socket reconnecting
              // (e.g. Android cutting the network of a backgrounded browser) must not restart it.
              const rejoined = this.reconnecting;
              this.reconnecting = false;
              this.reconnectAttempts = 0;

              if (rejoined && data.memberId === previousMemberId) {
                this.connecting = false;
                return;
              }

              const withStart =
                this.isModerated || rejoined || this.rejoinAfterReload
                  ? ''
                  : 'AndStart';
              this.connecting = !!withStart;
              if (withStart) {
                this.appShareRequested = true;
              }
              const deepLinkParams = new URLSearchParams();
              if (data.displayId) {
                deepLinkParams.set('displayId', data.displayId);
              }
              if (data.displayIds?.length) {
                deepLinkParams.set('displayIds', data.displayIds.join(','));
              }
              if (data.sessionId) {
                deepLinkParams.set('sessionId', data.sessionId);
              }
              if (data.memberId) {
                deepLinkParams.set('appsMemberId', data.memberId);
              }

              this.appJoined = true;
              this.rvshareAppDetectionService.issueDeepLink(
                `join${withStart}?${deepLinkParams.toString()}`,
              );
              return;
            }

            this.connecting = false;
            this.reconnecting = false;
            this.reconnectAttempts = 0;

            if (this.channelTimeout && this.displayParticipant) {
              console.log('Restarting stream');
              this.stream();

              return;
            }

            if (!this.isModerated && !this.mediaStream) {
              this.start();
            }

            break;

          case MessageType.INVALID_CHANNEL:
            console.warn('Invalid channel: ', data);
            if (this.moderator) {
              this.channelError =
                'Unable to start a moderated session on display ' +
                websocketParams.displayId +
                '.';
              this.joinedSubject.next(MessageType.ERROR);
            } else {
              this.joinCodeError =
                'Invalid join code: ' + websocketParams.joinCode + '.';
            }
            this.connecting = false;
            this.reset();
            break;

          case MessageType.ICECANDIDATE:
            if (data.memberId === this.memberId) {
              const displayPeerState = this.findDisplayPeerStateForSignal(
                data.displayId,
                data.senderId,
              );

              if (!displayPeerState) {
                console.warn(
                  'ICE candidate: could not resolve display peer (displayId=%s, senderId=%s).',
                  data.displayId,
                  data.senderId,
                );
                break;
              }

              if (displayPeerState.remoteDescriptionSet) {
                displayPeerState.peerConnection?.addIceCandidate(
                  data.candidate,
                );
              } else {
                console.log(
                  `Queuing ICE candidate (remote description not set yet) for display ${data.displayId}.`,
                );
                displayPeerState.pendingIceCandidates.push(data.candidate);
              }
            }
            break;

          case MessageType.OFFER:
            this.senderId =
              this.findParticipantIdFromMobileSender(data.senderId) ||
              data.senderId;
            break;

          case MessageType.ANSWER:
            console.log('Received answer');
            if (this.moderator && !this.senderId && this.invitedId) {
              this.senderId = this.invitedId;
            }
            this.invitedId = '';
            this.channelError = '';
            this.connecting = false;
            this.clearChannelTimeout();
            if (this.senderId === this.memberId) {
              (async () => {
                const displayPeerState = this.findDisplayPeerStateForSignal(
                  data.displayId,
                  data.senderId,
                );
                const peerConnection = displayPeerState?.peerConnection;

                if (!displayPeerState || !peerConnection) {
                  console.warn(
                    'Answer: could not resolve display peer (displayId=%s, senderId=%s).',
                    data.displayId,
                    data.senderId,
                  );
                  return;
                }

                // Displays return the offer's id with their answer. An answer to an older offer (one this connection
                // was replaced or restarted after) comes from a peer connection the display has closed, and would
                // keep the latest one's answer out. Displays that don't return the id yet are taken at their word.
                if (data.offerId && data.offerId !== displayPeerState.offerId) {
                  console.warn(
                    'Skipping answer: it answers an older offer for display %s.',
                    displayPeerState.displayId,
                  );
                  return;
                }

                displayPeerState.avoidLiveResolutionDownscale =
                  !!data.avoidLiveResolutionDownscale;

                const signalingState = peerConnection.signalingState;
                if (signalingState && signalingState !== 'have-local-offer') {
                  if (signalingState === 'stable') {
                    console.warn(
                      'Skipping answer: peer for display %s is already stable (duplicate or misrouted SDP).',
                      displayPeerState.displayId,
                    );
                  }
                  return;
                }
                if (displayPeerState.settingAnswer) {
                  console.warn(
                    'Skipping answer: the answer for display %s is already being set.',
                    displayPeerState.displayId,
                  );
                  return;
                }
                displayPeerState.settingAnswer = true;

                // A new connection can replace this one on the same state (stream()) while the answer is set; the
                // late answer must not touch it.
                const replaced = () =>
                  displayPeerState.peerConnection !== peerConnection;
                try {
                  await peerConnection.setRemoteDescription({
                    type: data.type as RTCSdpType,
                    sdp: data.sdp ?? '',
                  });
                } catch (e) {
                  console.error(
                    'setRemoteDescription failed for display',
                    displayPeerState.displayId,
                    e,
                  );
                  if (replaced()) {
                    return;
                  }
                  displayPeerState.settingAnswer = false;
                  // The connection can't carry media without the answer. This happens when a display that
                  // reloaded (and lost its peer connection) answers a recovery offer, so start over instead.
                  if (this.mediaStream) {
                    this.closePeerConnection(displayPeerState.displayId);
                    this.stream([displayPeerState.displayId]);
                  }
                  return;
                }
                if (replaced()) {
                  return;
                }

                displayPeerState.settingAnswer = false;
                displayPeerState.remoteDescriptionSet = true;
                displayPeerState.offerStartedAt = undefined;
                displayPeerState.recoveryWaiting = false;

                // The display rejoined with a different orientation while this offer waited for its answer, so the
                // new connection for its frame was held off until now. A display missing from the members (between
                // leaving and rejoining) has no orientation to go by.
                if (
                  displayPeerState.orientation &&
                  this.isDisplayParticipant(displayPeerState.displayId) &&
                  displayPeerState.orientation !==
                    this.getDisplayOrientation(displayPeerState.displayId)
                ) {
                  console.log(
                    'Display %s was rotated while its offer waited for its answer; rebuilding its connection.',
                    displayPeerState.displayId,
                  );
                  if (this.mediaStream) {
                    this.closePeerConnection(displayPeerState.displayId);
                    this.stream([displayPeerState.displayId]);
                  }
                  return;
                }

                console.log(
                  `Flushing ${displayPeerState.pendingIceCandidates.length} queued ICE candidates for display ${displayPeerState.displayId}.`,
                );
                for (const candidate of displayPeerState.pendingIceCandidates) {
                  // One the connection rejects must not keep the rest out
                  try {
                    await peerConnection.addIceCandidate(candidate);
                  } catch (error) {
                    console.warn(
                      'Failed to add a queued ICE candidate for display %s.',
                      displayPeerState.displayId,
                      error,
                    );
                  }
                }
                displayPeerState.pendingIceCandidates = [];
              })();
            }
            if (this.senderId === this.memberId || this.moderator) {
              this.joinedSubject.next(MessageType.ANSWER);
            }
            break;

          case MessageType.DISPLAY_WEBRTC_RECOVERY:
            // A display that rejoined without a peer connection asks for a stream, but only one we were sending:
            // without a connection for it the share is paused (or never reached it), so there is nothing to restore.
            if (
              data.rejoined &&
              data.displayId &&
              !this.displayPeerStates.find(
                (state) => state.displayId === data.displayId,
              )?.peerConnection
            ) {
              console.log(
                'Ignoring rejoin stream request from display %s: no connection to restore.',
                data.displayId,
              );
              break;
            }
            if (
              data.displayId &&
              this.canRestartStreamForDisplayRecovery(data.displayId)
            ) {
              console.log(
                'Display requested WebRTC recovery; restarting offer for display %s (forceFull=%s).',
                data.displayId,
                !!data.forceFullReconnect,
              );
              this.clearChannelTimeout();
              this.restartStreamForDisplay(data.displayId, {
                forceFullReconnect: data.forceFullReconnect,
              });
            } else if (data.displayId) {
              console.warn(
                'Ignoring display WebRTC recovery (not sharing or no active stream). displayId=%s memberId=%s senderId=%s hasMediaStream=%s',
                data.displayId,
                this.memberId,
                this.senderId,
                !!this.mediaStream,
              );
            }
            break;

          case MessageType.SENDER_STATS_REQUEST:
            if (data.displayId) {
              void this.sendSenderStats(data.displayId);
            }
            break;

          case MessageType.NEW_MEMBER: {
            // A display that takes more than a few seconds to come back (a reload on a slow player) has already had
            // its connection closed on `disconnected`, so an active sharer restarts for it too.
            const streaming =
              this.displayPeerStates.some((c) => !!c.peerConnection) ||
              (!!this.mediaStream && this.memberIsSharing && !this.paused);
            console.log('New member joined', data, 'streaming', streaming);

            this.filterParticipants(data.members);
            const rejoinedDisplay = this.displayParticipants.find(
              (member) => member.memberId === data.senderId,
            );
            const rejoinedDisplayId =
              rejoinedDisplay?.displayId ??
              (data.senderId === this.displayParticipant?.memberId
                ? this.displayParticipant?.displayId
                : undefined);

            // The display came back with a different orientation (it was rotated between portrait and landscape).
            // Its connection sends a frame of the old size, and an ICE restart would keep that track, so build a
            // new connection with the frame for the new orientation.
            const rejoinedState = this.displayPeerStates.find(
              (state) => state.displayId === rejoinedDisplayId,
            );
            if (
              rejoinedDisplayId &&
              rejoinedState?.peerConnection &&
              rejoinedState.orientation &&
              rejoinedState.orientation !==
                this.getDisplayOrientation(rejoinedDisplayId)
            ) {
              console.log(
                'Display %s rejoined with a new orientation; rebuilding its connection.',
                rejoinedDisplayId,
              );
              this.restartStreamForDisplay(rejoinedDisplayId, {
                forceFullReconnect: true,
              });
              break;
            }

            // The server kept the display in the session across a quick websocket reconnect (e.g. Cloud Run's
            // hourly request timeout). Its peer connection is still up, so rebuilding it would only blink the
            // screen. A display that lost its connection asks for a new one (display-webrtc-recovery).
            if (
              data.reconnected &&
              rejoinedDisplayId &&
              this.isPeerConnected(rejoinedDisplayId)
            ) {
              console.log(
                'Display %s reconnected with its peer connection up; keeping it.',
                rejoinedDisplayId,
              );
              break;
            }

            if (streaming && rejoinedDisplay?.displayId) {
              this.restartStreamForDisplay(rejoinedDisplay.displayId);
            } else if (
              streaming &&
              this.displayParticipant &&
              data.senderId === this.displayParticipant.memberId
            ) {
              this.restartStream();
            }

            break;
          }

          case MessageType.MODERATOR_NOT_ALLOWED:
            this.channelError =
              'This display is not configured for screen share moderation. Please check your display settings.';
            this.closing = true;
            this.joinedSubject.next(MessageType.ERROR);
            break;

          case MessageType.MODERATOR_NOT_AUTHENTICATED:
            this.channelError =
              'Authentication failed. Please log in and try again.';
            this.closing = true;
            this.joinedSubject.next(MessageType.ERROR);
            break;

          case MessageType.MEMBER_DISCONNECTED:
            this.filterParticipants(data.members);
            break;

          case MessageType.REMOVED_FROM_CHANNEL:
            this.channelError =
              data.reason ?? 'A moderator has removed you from the room.';
            // The server removed this member (a moderator, or nobody sharing for too long): release a capture kept
            // while paused, and tell the RV Share app to leave too. Not when this page is leaving already.
            if (!this.closing) {
              this.leave();
            }
            this.closing = true;
            this.joinedSubject.next(MessageType.ERROR);
            break;

          case MessageType.INVITE_SHARING:
            this.invitedSubject.next(true);
            break;

          case MessageType.IDLE_SESSION_WARNING:
            this.idleSessionEndsAt = Date.now() + (data.remainingMs ?? 0);
            break;

          case MessageType.IDLE_SESSION_ACTIVE:
            this.idleSessionEndsAt = null;
            break;

          case MessageType.STOP_SHARING:
            this.invitedSubject.next(false);
            this.clearSharingParticipantState(
              data.memberId || data.senderId || this.senderId,
            );
            this.closeStream();
            break;

          case MessageType.CLIENT_STREAM_CLOSED:
            this.clearSharingParticipantState(data.senderId);
            this.closeStream();
            if (data.senderId === this.invitedId) {
              this.invitedId = '';
            }
            break;
          case MessageType.DISPLAY_INTERRUPTED:
            this.channelError =
              'The screen sharing session has been interrupted.';
            this.closing = true;
            this.joinedSubject.next(MessageType.ERROR);
            break;
          case MessageType.CONNECTION_REJECTED:
            this.closing = true;
            this.connecting = false;
            this.channelError =
              'The connection was rejected because the session already has a streamer connected.';
            this.joinedSubject.next(MessageType.ERROR);
            break;
          case MessageType.CONNECTION_REJECTED_OTHERS_JOIN:
            this.closing = true;
            this.connecting = false;
            this.channelError =
              "The connection was rejected because the session doesn't allow others to join.";
            this.joinedSubject.next(MessageType.ERROR);
            break;
          case MessageType.VOLUME_MUTED:
            this.muted = true;
            break;
          case MessageType.SHARER_AUDIO:
            this.sharerAudio =
              toSharerAudio(data.senderId, data.status) ?? this.sharerAudio;
            break;
          case MessageType.CHANNEL_CONFIG:
            this.updateConfig(data.config);
            if (this.memberIsSharing && data.config?.quality) {
              void this.applyQualityChange();
            }
            break;
          case MessageType.REQUEST_PIN:
            this.joinedSubject.next(MessageType.REQUEST_PIN);
            this.connecting = false;
            this.closing = true;
            break;
          case MessageType.INVALID_SECURITY_PIN:
            this.joinCodeError = 'The PIN you entered is incorrect.';
            this.joinedSubject.next(MessageType.ERROR);
            this.connecting = false;
            this.closing = true;
            break;
          case MessageType.REQUEST_AUTHENTICATION:
            this.joinedSubject.next(MessageType.REQUEST_AUTHENTICATION);
            this.connecting = false;
            this.closing = true;
            break;
          case MessageType.INVALID_AUTHENTICATION:
            this.channelError =
              'There was something wrong with your authentication. Please try again.';
            this.joinedSubject.next(MessageType.ERROR);
            this.connecting = false;
            this.closing = true;
            break;
          case MessageType.INVALID_AUTHENTICATION_COMPANY:
            this.channelError =
              "You're not a user of the required company. Please contact your company's system administrator to add you as a user.";
            this.joinedSubject.next(MessageType.ERROR);
            this.connecting = false;
            this.closing = true;
            break;
          default:
            console.log('Unknown message', data);
        }
      },
      error: (e) => {
        console.log('WebSocket error:', e);
        this.channelError =
          'Connection could not be established. Please check your network connection.';
        this.joinedSubject.next(MessageType.ERROR);
        this.connecting = false;
        this.reset();
      },
      complete: () => {
        // Check if the socket is closed intentionally
        if (!this.closing) {
          this.channelError = 'The session has ended.';
          this.joinedSubject.next(MessageType.ERROR);
          this.closing = true;
        }

        console.log('WebSocket connection closed');
      },
    });
  }

  private closeStream() {
    this.senderId = '';
    this.appShareRequested = false;
    this.sharerAudio = null;
    // A display that still has to mute says so again when it answers the next share
    this.muted = false;
    this.joinedSubject.next(MessageType.CLIENT_STREAM_CLOSED);
  }

  connect(): Observable<SignalServerData> {
    this.destroy$ = new Subject<void>();

    return new Observable<SignalServerData>((subscriber) => {
      if (this.reconnecting && this.currentWebsocketParams.token) {
        this.currentWebsocketParams.token =
          this.userStateService.getAccessToken()?.access_token ||
          this.currentWebsocketParams.token;
      }
      const url = `${environment.SCREEN_SHARING_SERVICE_URL}?${this.currentWebsocketParams.asQueryParams()}`;
      this.connectToWebSocket(url, subscriber);
    }).pipe(
      takeUntil(this.destroy$),
      retryWhen((errors) =>
        errors.pipe(
          delayWhen(() => {
            console.log(
              `Retrying WebSocket connection in ${this.reconnectInterval}ms...`,
            );
            return timer(this.reconnectInterval * this.reconnectAttempts);
          }),
          scan(
            (retryCount, err) => {
              if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                throw err; // Throw error to stop retrying
              } else {
                console.log(
                  `WebSocket reconnecting attempt ${this.reconnectAttempts + 1}`,
                );
                this.reconnectAttempts++;
                return this.reconnectAttempts;
              }
            },
            (this.reconnectAttempts = 0),
          ), // Initial retry count
          catchError((err) => {
            console.error(
              `WebSocket retry attempts exhausted (${this.maxReconnectAttempts}).`,
            );
            throw err; // Rethrow the error to propagate it to the subscription's error handler
          }),
        ),
      ),
    );
  }

  private connectToWebSocket(
    url: string,
    subscriber: Subscriber<SignalServerData>,
  ): void {
    this.socket = this.webSocketService.webSocket({
      url,
      openObserver: {
        next: () => {
          console.log('WebSocket connection established');
        },
      },
      closeObserver: {
        next: () => {
          // Check if the socket is closed intentionally
          if (!this.closing) {
            // Try reconnecting once
            if (!this.reconnecting) {
              console.warn('Socket disconnected, reconnecting.');

              subscriber.error(new Error('Socket disconnected, reconnecting.'));

              this.reconnecting = true;

              return;
            }

            console.error('Socket disconnected, retrying failed.');
          }

          if (this.mediaStream) {
            this.stop();
          }
          this.destroy();
          // Always close subscriber, unless reconnecting
          subscriber.complete();
        },
      },
    });
    this.socketSubscription = this.socket.subscribe({
      next: (data) => subscriber.next(data),
      error: (err) => {
        console.log('WebSocket error:', err);

        subscriber.error(err);
      },
      complete: () => () => {
        // Do nothing; wait for reconnect to complete
      },
    });
  }

  destroy(): void {
    if (this.destroy$) {
      this.destroy$.next();
      this.destroy$.complete();
    }
  }

  start() {
    this.connecting = true;
    // Before the screen picker opens: the server hears nothing else from this share until its offer.
    this.reportSessionActivity();

    if (this.rvshareAppDetectionService.isScreenShareApp()) {
      this.appShareRequested = true;
      this.rvshareAppDetectionService.issueDeepLink('start');
      // The native share sheet is the progress UI. Cancelling it never answers, so
      // this page must not stay on the room spinner.
      this.connecting = false;
      return;
    }

    const mediaOptions = {
      video: this.videoConstraints,
      audio: {
        autoGainControl: false,
        echoCancellation: false,
        noiseSuppression: false,
      },
    };

    // A rejoin can start another capture while the picker is still open; only the latest one may share.
    const captureRequest = ++this.captureRequest;
    navigator.mediaDevices
      .getDisplayMedia(mediaOptions)
      .then(async (mediaStream: MediaStream) => {
        if (captureRequest !== this.captureRequest) {
          mediaStream.getTracks().forEach((track) => track.stop());
          return;
        }
        this.mediaStream = mediaStream;
        this.senderId = this.memberId;
        // The picker doesn't say why a share has no audio: unsupported for this source or browser, or left unticked
        this.reportSharerAudio(
          mediaStream.getTracks().some((track) => track.kind === 'audio')
            ? 'shared'
            : 'unavailable',
        );
        this.joinedSubject.next(MessageType.ANSWER);

        mediaStream.getTracks().forEach((track) => {
          track.addEventListener('ended', (e) => {
            console.log('track removed', e);
            // Only the first track to end, and only while this is still the stream being shared.
            if (this.mediaStream !== mediaStream) {
              return;
            }
            // Stop like the Stop button does, so the displays don't wait for the peer connection to time out.
            this.stop();
          });
        });

        await this.applyVideoResolution();
        if (captureRequest !== this.captureRequest) {
          mediaStream.getTracks().forEach((track) => track.stop());
          return;
        }
        this.stream();
      })
      .catch((e) => {
        console.log('getDisplayMedia error: ', e);
        if (captureRequest !== this.captureRequest) {
          return;
        }
        this.stop();
        this.joinedSubject.next(MessageType.JOINED_CHANNEL);
      })
      .finally(() => {
        this.connecting = false;
        const now = new Date();
        this.started = now.getTime();
        this.analyticsFactory.track('Screen Sharing Started', {
          timestamp: now.toISOString(),
        });
      });
  }

  // The server relays it to the others, and keeps it for members joining later
  private reportSharerAudio(status: SharerAudioStatus) {
    this.sharerAudio = { memberId: this.memberId, status };
    this.socket?.next({ type: MessageType.SHARER_AUDIO, status });
  }

  // Whether the member that reported sharerAudio is this page, or this page's RV Share app
  isOwnShare(memberId: string): boolean {
    return (
      !!memberId &&
      !!this.memberId &&
      (memberId === this.memberId ||
        this.findParticipantIdFromMobileSender(memberId) === this.memberId)
    );
  }

  changeVolume(value: number) {
    this.socket.next({
      type: MessageType.CHANNEL_CONFIG,
      config: { volume: value },
    });
  }

  async applyVideoResolution(
    options: { allowDownscale?: boolean } = {},
  ): Promise<void> {
    if (!this.mediaStream) {
      return;
    }

    // Shrinking the capture is safe once it's letterboxed into a fixed frame: the encoded size stays the same.
    const allowCaptureDownscale =
      options.allowDownscale ??
      (this.fixedFrames.size > 0 ||
        !this.displayPeerStates.some(
          (displayPeerState) => displayPeerState.avoidLiveResolutionDownscale,
        ));
    const qualityDisabled =
      this.rvshareAppDetectionService.shouldDisableVideoQualitySetting();
    const target = this.getVideoQualityTarget();

    await Promise.all(
      this.mediaStream.getTracks().map(async (track) => {
        if (track.kind !== 'video') {
          return;
        }

        track.contentHint = 'detail';

        if (qualityDisabled) {
          return;
        }

        const settings =
          typeof track.getSettings === 'function' ? track.getSettings() : {};
        const currentWidth = settings.width ?? 0;
        const currentHeight = settings.height ?? 0;
        const wouldShrink =
          !!target &&
          currentWidth > 0 &&
          currentHeight > 0 &&
          (target.width < currentWidth || target.height < currentHeight);

        if (!allowCaptureDownscale && wouldShrink) {
          return;
        }

        try {
          await track.applyConstraints(this.videoConstraints);
        } catch {
          // OverconstrainedError: encoder scaleResolutionDownBy still applies.
        }
      }),
    );

    this.displayPeerStates.forEach((displayPeerState) => {
      if (displayPeerState.peerConnection) {
        this.applyVideoSenderParameters(displayPeerState.peerConnection, {
          allowDownscale:
            options.allowDownscale ??
            !displayPeerState.avoidLiveResolutionDownscale,
        });
      }
    });
  }

  changeQuality(value: ChannelConfig['quality']) {
    this.quality = value;
    // In the RV Share app the app encodes the share, and hears of the quality from the server
    if (this.moderator || this.rvshareAppDetectionService.isScreenShareApp()) {
      this.socket.next({
        type: MessageType.CHANNEL_CONFIG,
        config: { quality: value },
      });
    }
    if (this.memberIsSharing) {
      void this.applyQualityChange();
    }
  }

  /**
   * Bitrate/framerate apply live. A fixed-frame stream must never change size mid-stream, so any display whose
   * connection was created at a different encode size than the current quality needs is restarted at the new size
   * (a brief pause on the display). This compares against each connection, not the previous quality, because the
   * quality selector's ngModel updates `quality` before `changeQuality` runs.
   */
  private async applyQualityChange(): Promise<void> {
    await this.applyVideoResolution();

    const encodeScale = this.getFixedFrameEncodeScale();
    const displayIds = this.displayPeerStates
      .filter(
        (displayPeerState) =>
          !!displayPeerState.peerConnection &&
          displayPeerState.encodeScale !== undefined &&
          displayPeerState.encodeScale !== encodeScale,
      )
      .map((displayPeerState) => displayPeerState.displayId);
    if (!displayIds.length) {
      return;
    }

    console.log(
      'Quality changed the encoded size; restarting display connections.',
      displayIds,
    );
    this.clearChannelTimeout();
    this.closePeerConnection(displayIds);
    this.stream(displayIds);
  }

  changeAllowOthersToJoin(value: boolean) {
    this.allowOthersToJoin = value;
    this.socket?.next({
      type: MessageType.CHANNEL_CONFIG,
      allowOthersToJoin: value,
    });
  }

  interrupt() {
    this.channelError = 'The connection was interrupted, please try again.';
    this.closePeerConnection();
    if (this.isModerated) {
      this.resetChannel();
    } else {
      this.reset();
    }
  }

  clearChannelTimeout() {
    clearTimeout(this.channelTimeout);
    this.channelTimeout = null;
  }

  restartStream() {
    console.log('Restarting stream');

    this.closePeerConnection();
    this.stream();
  }

  stream(displayIds: string[] = this.getTargetDisplayIds()) {
    if (
      this.rvshareAppDetectionService.isScreenShareApp() ||
      !this.mediaStream
    ) {
      return;
    }
    this.paused = false;

    const targetDisplayIds = [...new Set(displayIds.filter(Boolean))];
    if (targetDisplayIds.length === 0) {
      return;
    }

    const targetDisplayIdSet = new Set(targetDisplayIds);
    targetDisplayIds.forEach((displayId) => {
      if (
        !this.displayPeerStates.find((conn) => conn.displayId === displayId)
      ) {
        this.displayPeerStates.push({
          displayId,
          peerConnection: null,
          remoteDescriptionSet: false,
          pendingIceCandidates: [],
          avoidLiveResolutionDownscale: false,
        });
      }
    });

    this.displayPeerStates
      .filter((conn) => targetDisplayIdSet.has(conn.displayId))
      .forEach((displayPeerState) => {
        displayPeerState.remoteDescriptionSet = false;
        displayPeerState.pendingIceCandidates = [];
        displayPeerState.offerStartedAt = performance.now();
        displayPeerState.recoveryWaiting = false;
        displayPeerState.settingAnswer = false;
        displayPeerState.recoveryOfferInFlight = false;
        const offerId = crypto.randomUUID();
        displayPeerState.offerId = offerId;
        displayPeerState.peerConnection?.close();
        displayPeerState.peerConnection = new RTCPeerConnection(
          this.iceConfiguration,
        );
        displayPeerState.peerConnection.addEventListener(
          'connectionstatechange',
          (event?: Event) => {
            const pc = displayPeerState.peerConnection;
            const connectionState = pc?.connectionState;

            console.log(
              'peerConnection.connectionstatechange',
              pc?.connectionState,
              {
                connectionState: pc?.connectionState,
                displayParticipantMemberId: this.displayParticipant?.memberId,
                iceConnectionState: pc?.iceConnectionState,
                signalingState: pc?.signalingState,
                iceGatheringState: pc?.iceGatheringState,
                event,
              },
            );

            switch (connectionState) {
              case 'new':
              case 'connecting':
                break;
              case 'connected':
                // Give the new connection the last frame now rather than on the next keepalive, up to a second later.
                if (pc) {
                  this.fixedFrameVideoOf(pc)?.refresh();
                }
                this.endDisplayReconnect(displayPeerState.displayId);
                break;
              case 'disconnected':
              case 'failed':
                // The display only notices a lost connection 10s after it goes 'disconnected', and asks for a new one
                // 3s after that, so replacing it here brings the share back as soon as the network does.
                this.scheduleDisplayReconnect(
                  displayPeerState.displayId,
                  pc,
                  connectionState === 'disconnected'
                    ? ScreenSharingService.DISCONNECTED_GRACE_MS
                    : 0,
                );
                if (!this.channelTimeout) {
                  this.channelError =
                    'The connection was interrupted, sharing will resume when the display reconnects.';
                  this.channelTimeout = setTimeout(
                    () => this.interrupt(),
                    180_000,
                  );
                }
                break;
              case 'closed':
                this.closePeerConnection(displayPeerState.displayId);
                break;
            }
          },
          false,
        );

        displayPeerState.peerConnection.addEventListener(
          MessageType.ICECANDIDATE,
          (e) => {
            const candidate = e.candidate
              ? {
                  candidate: e.candidate.candidate,
                  sdpMid: e.candidate.sdpMid,
                  sdpMLineIndex: e.candidate.sdpMLineIndex,
                }
              : null;
            this.socket.next({
              type: MessageType.ICECANDIDATE,
              candidate,
              displayId: displayPeerState.displayId,
            });
          },
        );

        const outboundStream = this.getOutboundStream(
          displayPeerState.displayId,
        );
        outboundStream
          .getTracks()
          .forEach((track) =>
            displayPeerState.peerConnection.addTrack(track, outboundStream),
          );

        this.preferHardwareVideoCodec(displayPeerState.peerConnection);
        this.applyVideoSenderParameters(displayPeerState.peerConnection, {
          initial: true,
        });
        displayPeerState.encodeScale =
          outboundStream === this.mediaStream
            ? undefined
            : this.getFixedFrameEncodeScale();
        displayPeerState.orientation =
          outboundStream === this.mediaStream
            ? undefined
            : this.getDisplayOrientation(displayPeerState.displayId);

        const peerConnection = displayPeerState.peerConnection;
        if (!peerConnection.createOffer) {
          return;
        }
        // A newer connection (or the share stopping) can replace this one while its offer is made; then stop quietly.
        const isCurrent = () =>
          this.displayPeerStates.includes(displayPeerState) &&
          displayPeerState.peerConnection === peerConnection;
        peerConnection
          .createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true })
          .then(async (offer) => {
            if (!isCurrent()) {
              return;
            }
            if (!offer?.sdp) {
              throw new Error('Offer missing SDP');
            }
            if (peerConnection.setLocalDescription) {
              await peerConnection.setLocalDescription(offer);
            }
            if (!isCurrent()) {
              return;
            }
            this.socket.next({
              type: MessageType.OFFER,
              sdp: offer.sdp,
              displayId: displayPeerState.displayId,
              offerId,
            });
          })
          .catch((error) => {
            // An offer started on this connection since then has its own wait
            if (!isCurrent() || displayPeerState.offerId !== offerId) {
              return;
            }
            console.warn(
              'Offer failed for display %s.',
              displayPeerState.displayId,
              error,
            );
            // No answer can come for an offer that wasn't sent, so recovery requests don't wait for one, and one held
            // meanwhile goes ahead now.
            displayPeerState.offerStartedAt = undefined;
            if (displayPeerState.recoveryWaiting) {
              displayPeerState.recoveryWaiting = false;
              if (this.mediaStream) {
                this.closePeerConnection(displayPeerState.displayId);
                this.stream([displayPeerState.displayId]);
              }
            }
          });
      });
  }

  remove(memberId: string) {
    if (this.senderId === memberId) {
      this.stopSharing();
    }
    setTimeout(() => {
      this.socket.next({
        type: MessageType.REMOVE_FROM_CHANNEL,
        memberId,
        reason: 'A moderator has removed you from the room.',
      });
    }, 1000);
  }

  invite(memberId: string) {
    this.invitedId = memberId;
    this.socket.next({ type: MessageType.INVITE_SHARING, memberId });
  }

  stopSharing() {
    this.socket.next({
      type: MessageType.STOP_SHARING,
      memberId: this.senderId,
    });
  }

  pause() {
    this.paused = true;
    this.closePeerConnection();
    // The RV Share app sends the stream: it closes its connections and tells the displays.
    if (this.rvshareAppDetectionService.isScreenShareApp()) {
      this.rvshareAppDetectionService.issueDeepLink('pause');
      return;
    }
    this.socket?.next({ type: MessageType.CLIENT_STREAM_PAUSED });
  }

  resume() {
    if (this.rvshareAppDetectionService.isScreenShareApp()) {
      this.paused = false;
      this.rvshareAppDetectionService.issueDeepLink('resume');
      return;
    }
    this.stream();
  }

  // leaving: the app's leave deep link follows, which also stops its share. Chrome asks the user before opening the app
  // a second time in a row, so the stop deep link is left out.
  stop(leaving = false) {
    this.paused = false;
    this.socket?.next({
      type: MessageType.STOP_SHARING,
      memberId: this.senderId || this.memberId,
    });

    this.closeStream();
    this.invitedId = '';

    this.stopAll();

    if (
      this.rvshareAppDetectionService.isScreenShareApp() &&
      !this.appAlreadyClosed &&
      !leaving
    ) {
      this.rvshareAppDetectionService.issueDeepLink('stop');
    }
  }

  // The RV Share app reported an error, and it has already stopped and left before reopening this page. Reset only
  // this page's session: stop/leave deep links back to the app would interrupt it reopening the page, and Chrome then
  // closes the page.
  resetAfterAppError() {
    this.appAlreadyClosed = true;
    try {
      this.reset();
    } finally {
      this.appAlreadyClosed = false;
    }
  }

  // Tells the server the session is in use, so it doesn't end it for nobody sharing. The warning stays until the server
  // takes it down, so one lost to a reconnect stays up for another click.
  reportSessionActivity() {
    this.socket?.next({ type: MessageType.SESSION_ACTIVITY });
  }

  leave(endModeratedSession: boolean = false) {
    this.captureRequest++;
    // The session can outlive this member, but their share ends with them. In the RV Share app the app sends the
    // stream, and while it is paused no peer connection is left whose closing would end it on the displays.
    if (
      this.mediaStream ||
      (this.rvshareAppDetectionService.isScreenShareApp() &&
        (this.memberIsSharing || this.appShareRequested))
    ) {
      this.stop(true);
    }
    this.closing = true;
    this.idleSessionEndsAt = null;
    if (endModeratedSession) {
      this.socket?.next({
        type: MessageType.RETURN_TO_STANDARD,
        reason: 'A moderator closed the session.',
      });
      this.localStorageHelperService.remove(
        ModeratorWebsocketParamsService.MODERATOR_SESSION_KEY,
      );
      this.allowOthersToJoin = false;
    }
    this.participants = [];
    this.joinedSubject.next(MessageType.CHANNEL_CLOSED);

    // Only a page that joined has a session in the app to leave. The join page leaves on its way to the moderator
    // page too, and a needless deep link there stops the moderator page's first requests, or closes the Android app.
    if (
      this.rvshareAppDetectionService.isScreenShareApp() &&
      !this.appAlreadyClosed &&
      this.appJoined
    ) {
      this.rvshareAppDetectionService.issueDeepLink('leave');
    }
    this.appJoined = false;

    setTimeout(() => {
      this.socketSubscription?.unsubscribe();
      if (this.socket) this.socket.complete();
    }, 500);
  }

  reset(endModeratedSession: boolean = false) {
    this.closing = true;
    this.stop(true);
    this.destroy();
    this.muted = false;

    this.leave(endModeratedSession);
  }

  resetChannel() {
    this.socket.next({ type: MessageType.RESET_CHANNEL });
    this.reset(!!this.moderator);
  }

  closePeerConnection(displayIds?: string | string[]) {
    const targetDisplayIds = displayIds
      ? new Set(Array.isArray(displayIds) ? displayIds : [displayIds])
      : null;
    // Closing every connection (a pause, stop or interruption) ends any reconnect; closing one is part of replacing it.
    if (!targetDisplayIds) {
      this.clearDisplayReconnects();
    }
    let hadConnection = false;
    this.displayPeerStates = this.displayPeerStates.filter(
      (displayPeerState) => {
        if (
          targetDisplayIds &&
          !targetDisplayIds.has(displayPeerState.displayId)
        ) {
          return true;
        }

        if (displayPeerState.peerConnection) {
          hadConnection = true;
          try {
            displayPeerState.peerConnection.close();
            displayPeerState.pendingIceCandidates = [];
            displayPeerState.remoteDescriptionSet = false;
          } catch (e) {
            console.error(
              'Error closing peerConnection for display',
              displayPeerState.displayId,
              e,
            );
          }
          displayPeerState.peerConnection = null;
        }
        this.senderStatsSamples.delete(displayPeerState.displayId);

        return targetDisplayIds ? false : false;
      },
    );
    if (hadConnection && !targetDisplayIds) {
      const now = new Date();
      this.analyticsFactory.track('Screen Sharing Ended', {
        timestamp: now.toISOString(),
        duration: Math.round((now.getTime() - this.started) / 1000),
      });
    }
  }

  clearErrors() {
    this.channelError = '';
  }

  stopAll() {
    this.stopFixedFrames();
    this.mediaStream?.getTracks().forEach((t) => t.stop());
    this.mediaStream = null;
    this.closePeerConnection();
    this.displayPeerStates = [];
  }
}
